/**
 * Admin page for managing employees, groups, roles, permissions, and companies,
 * with tab navigation, search, and permission-gated feature rendering.
 *
 * @module pages/admin/Admin
 * @author Covintus
 * @author Snowfly
 * @version 1.0.0
 */
import React, {useEffect, useState} from 'react';
import Paper from '@material-ui/core/Paper';
import {connect} from 'react-redux';
import {makeStyles} from '@material-ui/core/styles';
import Toolbar from '@material-ui/core/Toolbar';
import SearchIcon from '@material-ui/icons/Search';
import InputBase from '@material-ui/core/InputBase';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import {Alert} from '@material-ui/lab';
import {AppBar, Tooltip,} from '@material-ui/core';
import Tab from '@material-ui/core/Tab';
import TabPanel from '@material-ui/lab/TabPanel';
import TabContext from '@material-ui/lab/TabContext';
import TabList from '@material-ui/lab/TabList';
import PropTypes from 'prop-types';
import Breadcrumbs from '@material-ui/core/Breadcrumbs';
import {Redirect} from 'react-router-dom';
import StyledLink from '../../components/common/StyledLink';
import EmployeeList from '../../components/admin/EmployeeList.jsx';
import Roles from '../../components/admin/Roles.jsx';
import Groups from '../../components/admin/Groups.jsx';
import {fetchEmployeeList, fetchGroupsList} from '../../redux/sims/actions';
import Permissions from '../../components/admin/Permissions.jsx';
import CreateUser from '../../components/admin/CreateUser.jsx';
import CreateGroup from '../../components/admin/CreateGroup.jsx';
import {savePermission} from '../../services/result.service';
import {fetchPermissionDetails} from '../../redux/authentication/action';
import {reset} from '../../redux/admin/action';
import AdminsPermissions from '../../permissions/admin';
import {permissionCheck} from '../../utils/permissionCheck';
import AdminStyle from './Admin';
import Notification from '../../components/admin/Notification.jsx';
import CompanyTable from "../../components/admin/CompanyTable.jsx";
import ErrorBoundary from "../../utils/ErrorBoundary";
import store from "../../redux/store";

/**
 * Represents the Admin component that manages the functionality for handling
 *      employees, groups, roles, permissions, and other administrative features
 *      within the application.
 *
 * The Admin component is responsible for a variety of administrative tasks,
 *      including managing employee and group data, handling role and
 *      permission-related actions, and handling UI interactions for tab
 *      navigation, search, and state updates.
 * It achieves this by interacting with several APIs, using hooks to manage local
 *      state, and checking permissions to conditionally render features.
 *
 * @param {Object} params - The parameters passed to the Admin component.
 * @param {Function} params.fetchEmployeeList - A function used to retrieve the
 *      list of employees.
 * @param {Array} params.employeeList - An array containing the fetched list of
 *      employees.
 * @param {number} params.employeeTotal - Represents the total number of employees.
 * @param {Function} params.fetchGroupsList - A function used to retrieve the list
 *      of groups.
 * @param {Array} params.groups - An array containing the fetched list of groups.
 * @param {number} params.groupTotal - Represents the total number of groups.
 * @param {boolean} params.prop - A boolean value usually used to trigger specific
 *      effects or actions.
 * @param {Function} params.fetchPermissionDetails - A function used to fetch
 *      permission-related information.
 * @param {Function} params.reset - A function used to reset or clear any
 *      temporary data or state.
 * @param {Object} params.userDetails - An object containing details about the
 *      logged-in user.
 */
const Admin = ({
    fetchEmployeeList,
    employeeList,
    employeeTotal,
    fetchGroupsList,
    groups,
    groupTotal,
    prop,
    fetchPermissionDetails,
    reset,
    userDetails,
}) => {
    const [value, setValue] = React.useState('0');
    const [enableCreateUser, setEnableCreateUser] = React.useState(false);
    const [empList, setEmpList] = useState([]);
    const [pageNumber, setPageNumber] = useState(0);
    const [textSearch, setTextSearch] = useState('');
    const [createNewGroup, setCreateNewGroup] = React.useState(false);
    const [groupList, setGroupList] = useState([]);
    const [flag, setFlag] = useState(true);
    const [text, setText] = useState('');
    const [refreshPermission, setRefreshPermission] = useState(false);
    const [save, setSave] = useState(false);
    const [permissionData, setPermissionData] = useState({});
    const [tst, setTst] = useState(false);
    const [saveTmpRole, setTmpRole] = useState(false);
    const [roles, setRoles] = useState([]);
    const [edit, setEdit] = useState(false);
    const [notification, setNotification] = useState(false);
    const [sortBy, setSortBy] = React.useState(null);
    const [order, setOrder] = React.useState(null);
    const [showEditCompanyModal, setShowEditCompanyModal] = useState(false);
    const useStyles = makeStyles((theme) => AdminStyle(theme));
    const classes = useStyles();

    const data = [];
    const state = store.getState();
    const employeeListEnable = permissionCheck(
        AdminsPermissions.employee_list.view.task,
        AdminsPermissions.employee_list.view.type,
    );
    const groupListEnable = permissionCheck(
        AdminsPermissions.groups.view.task,
        AdminsPermissions.groups.view.type,
    );
    const permissionsListEnable = permissionCheck(
        AdminsPermissions.permissions.view.task,
        AdminsPermissions.permissions.view.type,
    );
    const roleListEnable = permissionCheck(
        AdminsPermissions.roles.view.task,
        AdminsPermissions.roles.view.type,
    );
    const companyCreateEnable = permissionCheck(
        AdminsPermissions.companies.create.task,
        AdminsPermissions.companies.create.type
    ) || state?.login?.user?.super_admin;

    localStorage.setItem('sims_id', '');

    useEffect(
        () => {
            if (employeeListEnable) {
                setValue('0');
            } else if (groupListEnable) {
                setValue('2');
            } else if (permissionsListEnable) {
                setValue('3');
            } else if (roleListEnable) {
                setValue('4');
            } else if(companyCreateEnable) {
                setValue('6');
            }
        },
        [
            setValue, employeeListEnable, groupListEnable, roleListEnable,
            permissionsListEnable, companyCreateEnable
        ]
    );

    useEffect(
        () => {
            if (textSearch) {
                setPageNumber(0);
                if (value === '2') {
                    setGroupList([]);
                    fetchGroupsList(pageNumber, textSearch, sortBy, order);
                } else {
                    fetchEmployeeList(pageNumber, textSearch, sortBy, order);
                }
            } else if (value === '2') {
                setGroupList([]);
                fetchGroupsList(pageNumber, '', sortBy, order);
            } else if (permissionCheck(
                AdminsPermissions.employee_list.view.task,
                AdminsPermissions.employee_list.view.type,
            )) {
                fetchEmployeeList(pageNumber, textSearch, sortBy, order);
            }
        },
        [
            pageNumber,
            textSearch,
            fetchEmployeeList,
            fetchGroupsList,
            employeeListEnable,
            value,
            sortBy,
            order
        ]
    );

    useEffect(() => {
        if (value === '2') {
            setGroupList((prev) => [...prev, ...groups]);
        } else if (employeeList.length > 0 && flag) {
            setFlag(false);
            setEmpList((prev) => [...prev, ...employeeList]);
        } else if (saveTmpRole) {
            setEmpList((prev) => [...prev, ...employeeList]);
        }
    }, [employeeList, groups]);

    useEffect(() => {
        reset();
    }, [reset, value]);

    useEffect(() => {
        if (prop) {
            setCreateNewGroup(true);
        }
    }, [prop]);

    setTimeout(() => {
        setSave(false);
    }, 5000);

    useEffect(() => {
        // const componentDidUpdate = () => {
        if (tst) {
            window.onbeforeunload = () => true;
        } else {
            window.onbeforeunload = undefined;
        }
        // }
    }, [tst]);

    /**
     * Toggles the state of the `edit` variable between true and false.
     * This function is typically used to handle the click event for editing operations.
     */
    const editClick = () => {
        setEdit(!edit);
    };

    /**
     * Handles tab change logic by resetting form states and confirming unsaved changes.
     *
     * This function is designed to manage the behavior of switching between tabs
     * in the application. Depending on whether there is unsaved permission data,
     * it prompts the user for confirmation before handling the tab change. If no
     * permission data exists, it resets various states related to employee list,
     * group list, and input fields for the new tab.
     *
     * @param {Object} event - The event object triggered by the tab change action.
     * @param {string} val - The value representing the target tab.
     */
    const handleTabs = (event, val) => {
        setRoles([]);
        reset();

        if (Object.keys(permissionData).length === 0) {
            setEmpList([]);
            setGroupList([]);
            setCreateNewGroup(false);
            setPageNumber(0);
            setFlag(true);
            setTextSearch('');
            setText('');
            setValue(val);
        } else {
            const confirm = window.confirm(
                'Changes that you made may not be saved.'
            );
            if (confirm) {
                setValue(val);
                setPermissionData({});
                setTst(false);
            }
        }
    };

    /**
     * Handles search functionality based on user input events.
     *
     * This function is triggered either when the 'Enter' key is pressed
     * or when a 'mousedown' event occurs. It updates the search text,
     * toggles a flag, and clears employee and group lists.
     *
     * @param {Object} event - The event object associated with the user's input action.
     * @param {string} event.key - The key value of the event (e.g., 'Enter').
     * @param {string} event.type - The type of event (e.g., 'mousedown').
     */
    const searchData = (event) => {
        if (event.key === 'Enter' || (event.type === 'mousedown')) {
            setTextSearch(text);
            setFlag(true);
            setEmpList([]);
            setGroupList([]);
        }
    };

    /**
     * Advances to the next page and sets a tracking flag.
     *
     * This function is typically used to increment the current page number
     * by 1 and activate a flag indicating that a page transition has occurred.
     * It updates the necessary state to reflect these changes.
     */
    const handleNext = () => {
        setPageNumber((prevPageNumber) => prevPageNumber + 1);
        setFlag(true);
    };

    /**
     * Resets and initializes values for creating a new user.
     *
     * This function performs the following actions in sequence:
     * 1. Clears the employee list.
     * 2. Resets the current value to '0'.
     * 3. Enables the create user mode.
     * 4. Resets the page number to 0.
     *
     * It is intended to prepare the environment for the user creation process.
     */
    const createUser = () => {
        setEmpList([]);
        setValue('0');
        setEnableCreateUser(true);
        setPageNumber(0);
    };

    /**
     * Resets the group list, page number, and new group creation flag, then fetches the updated list of groups.
     *
     * This function performs the following operations:
     * - Clears the current list of groups by resetting it to an empty array.
     * - Resets the page number to 0.
     * - Sets the flag indicating new group creation to false.
     * - Triggers fetching of the group list based on the current page number.
     *
     * It is typically used to initialize or reset the state related to group management.
     */
    const createGroup = () => {
        setGroupList([]);
        setPageNumber(0);
        setCreateNewGroup(false);
        fetchGroupsList(pageNumber);
    };

    /**
     * Function to handle the creation of a new company.
     * This function triggers the display of the "Edit Company" modal by updating the relevant state variable.
     */
    const createCompany = () => {
        setShowEditCompanyModal(true);
    };

    /**
     * Handles the creation of user data and performs subsequent updates to the application's state.
     *
     * This function is responsible for saving new user data to the backend, updating various state values,
     * and fetching an updated employee list based on the current parameters.
     *
     * Updates performed:
     * - Sets a flag to true.
     * - Clears the employee list state.
     * - Resets a specific value to '0'.
     * - Disables the "Create User" functionality.
     * - Fetches an updated list of employees with the current pagination, search, and sorting parameters.
     */
    const createUserData = () => {
        // save new user data to backend
        setFlag(true);
        setEmpList([]);
        setValue('0');
        setEnableCreateUser(false);
        fetchEmployeeList(pageNumber, textSearch, sortBy, order);
    };

    /**
     * Handles the change in permissions for a specific role and task.
     * This method synchronizes the permissions data by adding or removing a permission
     * based on the current state, ensuring consistency with the "view" permission if applicable.
     *
     * @param {Object} role - The role object containing information about the role and its permissions.
     * @param {Object} task - The task object containing associated permissions for the role.
     * @param {string} permission - The specific permission type being toggled.
     */
    const handleChange = (role, task, permission) => {
        setTst(true);
        const perm = task.permissions.find((obj) => obj.permission_type === permission);
        const viewData = task.permissions.find((obj) => obj.permission_type === 'view');
        if (data.length) {
            for (let i = 0; i < data.length; i += 1) {
                if (data[i].role_id === role._id) {
                    const index = data[i].permissions.indexOf(perm.permission_id);
                    if (index > -1) {
                        data[i].permissions.splice(index, 1);
                    } else {
                        data[i].permissions.push(perm.permission_id);
                        if (viewData && data[i].permissions.indexOf(viewData.permission_id) === -1) {
                            data[i].permissions.push(viewData.permission_id);
                        }
                    }
                } else {
                    const permissions = [];
                    // eslint-disable-next-line array-callback-return
                    role.permission_details.map((perm) => {
                        if (Object.keys(perm).length !== 0) {
                            permissions.push(perm.permission_id);
                            if (viewData && permissions.indexOf(viewData.permission_id) === -1) {
                                permissions.push(viewData.permission_id);
                            }
                        }
                    });
                    data.push({
                        role_id: role._id,
                        permissions,
                    });
                }
            }
        } else {
            const permissions = [];
            // eslint-disable-next-line array-callback-return
            role.permission_details.map((perm) => {
                if (Object.keys(perm).length !== 0) {
                    permissions.push(perm.permission_id);
                }
            });
            data.push({
                role_id: role._id,
                permissions,
            });
            if (data.length) {
                const index = data[0].permissions.indexOf(perm.permission_id);
                if (index > -1) {
                    data[0].permissions.splice(index, 1);
                } else {
                    data[0].permissions.push(perm.permission_id);
                    if (viewData && data[0].permissions.indexOf(viewData.permission_id) === -1) {
                        data[0].permissions.push(viewData.permission_id);
                    }
                }
            }
        }
        setPermissionData(data);
    };

    /**
     * Function to save user permissions to a server.
     *
     * This function generates a header and body object containing the necessary
     * data for saving permissions, sends it to a server using `savePermission`,
     * and updates the state variables accordingly based on the server response.
     *
     * Actions performed by this function:
     * 1. Constructs a header object containing task and type information.
     * 2. Prepares a body object with current permission data.
     * 3. Calls `savePermission` to persist data to the server.
     * 4. On successful response, updates relevant states and resets specific fields.
     *
     * State variables affected:
     * - `setTst`: Reset to `false` at the start.
     * - `setRefreshPermission`: Set to `true` on successful save.
     * - `setSave`: Indicates save completion as `true`.
     * - `setPermissionData`: Clears the permission data.
     * - `setValue`: Resets a specific value, e.g., selection or navigation state.
     *
     * Dependencies:
     * - `savePermission`: An external function handling server communication.
     * - `fetchPermissionDetails`: Fetches updated permission details after saving.
     *
     * Assumptions:
     * - `permissionData` is defined and contains the data to be saved.
     */
    const savePermissions = () => {
        setTst(false);
        const header = {
            task: 'Permissions',
            type: 'create',
        };
        const body = {
            permission_data: permissionData,
        };
        savePermission(body, header).then(async (res) => {
            if (res) {
                setRefreshPermission(true);
                await fetchPermissionDetails();
                setSave(true);
                setPermissionData({});
                setValue('3');
            }
        });
    };

    /**
     * Function to add a temporary role to the employee management system.
     * Clears the current employee list and sets a temporary role flag.
     * Fetches a refreshed employee list starting from the first page.
     */
    const addTempRole = () => {
        setEmpList([]);
        setTmpRole(true);
        fetchEmployeeList(0);
    };

    /**
     * Handles the processing and storage of permissions based on a given row's
     * role data. Extracts roles from the provided row object, including temporary
     * roles if available, and prepares an array of role identifiers.
     *
     * @function
     * @param {Object} row - The input object containing role information.
     * @param {number|string} row.role_id - Primary role identifier associated with the row.
     * @param {Array<Object>} [row.tempRoles] - Optional array of temporary roles. Each
     *    temporary role object is expected to have a 'role' property.
     */
    const viewPermission = (row) => {
        const data = [];
        data.push(row.role_id);
        if (row.tempRoles && row.tempRoles.length) {
            // eslint-disable-next-line array-callback-return
            row.tempRoles.map((role) => {
                data.push((role.role).toString());
            });
        }
        setRoles(data);
        setValue('3');
    };

    /**
     * Deletes the current employee by resetting the employee list,
     * updating the temporary role flag, and fetching the initial employee list.
     *
     * The function performs the following actions:
     * 1. Clears the list of employees by setting it to an empty array.
     * 2. Activates or flags a temporary role using a boolean value.
     * 3. Fetches the employee list starting from the initial page or index (0).
     */
    const deleteEmployee = () => {
        setEmpList([]);
        setTmpRole(true);
        fetchEmployeeList(0);
    };

    /**
     * Closes the notification component.
     *
     * This function is used to set the notification state to 'false',
     * effectively closing or hiding the notification from the user interface.
     */
    const notificationClose = () => {
        setNotification(false);
    };

    /**
     * Function to handle sorting based on a specified property.
     *
     * This function determines the current sorting order for a given property
     * and toggles between ascending ('asc') and descending ('desc') order.
     * If the current sort property matches the provided property and if the
     * order is currently 'asc', it sets the order to 'desc'. Otherwise, it
     * sets the order to 'asc'.
     *
     * @param {string} property - The name of the property to sort by.
     * @returns {string} - The new sorting order ('asc' or 'desc').
     */
    const handleRequestSort = (property) => {
        const isAsc = sortBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        return isAsc ? 'desc' : 'asc';
    };

    /**
     * Updates the sorting behavior and resets associated state variables.
     *
     * The function handles the sorting logic by updating the relevant state variables,
     * resetting the pagination to the first page, and setting a sorting flag.
     *
     * @function
     * @param {string} property - The property by which sorting needs to be performed.
     */
    const sortProperties = (property) => {
        handleRequestSort(property);
        setPageNumber(0);
        setSortBy(property);
        setEmpList([]);
        setGroupList([]);
        setFlag(true);
    };

    /**
     * Determines and displays a search bar based on the provided value and certain conditions.
     *
     * The functionality dynamically checks the `value` parameter to decide the placeholder text
     * and whether the search bar should be rendered. It also adds input behavior for searching,
     * handling input changes, and executing search actions on specific events.
     *
     * Conditions for displaying the search bar:
     * - The `value` must be one of '0', '2', or '6'.
     * - The `enableCreateUser` and `createNewGroup` flags must both be `false`.
     *
     * Placeholder text for the search bar:
     * - '0': "Search available members"
     * - '2': "Search group names"
     * - '6': "Search Company Names"
     *
     * @param {string} value - A string identifier dictating the behavior and placeholder text of the search bar.
     * @returns {JSX.Element|null} - A JSX search bar element if conditions are met, otherwise `null`.
     */
    const showSearchBar = (value) => {
        const canShowSearchbar = (
            (value === '0') ||
            (value === '2') ||
            (companyCreateEnable && (value === '6'))
        );

        return (
            ((!enableCreateUser && !createNewGroup) && canShowSearchbar) ?
                (
                    <>
                        <div className={classes.search}>
                            <InputBase
                                placeholder={value === '0' ? 'Search available members' : (value === '2' ? 'Search group names' : 'Search Company Names')}
                                classes={{
                                    root: classes.inputRoot,
                                    input: classes.inputInput,
                                }}
                                value={text}
                                inputProps={{'aria-label': 'search'}}
                                onChange={(e) => setText(e.target.value)}
                                onKeyDown={(e) => searchData(e)}
                            />
                            <div>
                                <Button onMouseDown={(e) => searchData(e)}><SearchIcon/></Button>
                            </div>
                        </div>
                    </>
                )
                : null
        );
    };

    /**
     * Renders the secondary bar UI based on the provided value and user permissions.
     *
     * Depending on the `value` parameter, this function evaluates different use cases,
     * such as permissions to create users, groups, notifications, or companies. Also,
     * it includes a success message display for saved actions. The rendered elements
     * are contextually displayed UI components (e.g., Buttons, Tooltips, Alerts, etc.)
     * based on the provided input and the user's permissions.
     *
     * @param {string} value - A string representing the current selection or state.
     *                         Different values determine which secondary bar UI is displayed.
     *                         Valid options include:
     *                          - '0': Displays the search bar and, if permitted, an "Add New User" button.
     *                          - '2': Displays the search bar and an "Add New Group" button if permitted.
     *                          - '3': Displays a success alert for saved permissions.
     *                          - '5': Displays a button for adding new notifications.
     *                          - '6': Displays the search bar and, if permitted, an "Add New Company" button.
     * @returns {React.ReactNode|null} The rendered UI elements or null if the value does not match any case.
     */
    const showSecondaryBar = (value) => {
        const canCreateGroups = permissionCheck(
            AdminsPermissions.groups.create.task,
            AdminsPermissions.groups.create.type,
        );

        switch (parseInt(value)) {
            case 0:
                if (!enableCreateUser) {
                    const canCreateUsers = permissionCheck(
                        AdminsPermissions.employee_list.create.task,
                        AdminsPermissions.employee_list.create.type,
                    );

                    return (
                        <>
                            {showSearchBar(value)}
                            <StyledLink to="#">
                                <Tooltip
                                    title={canCreateUsers ? '' : "You don't have permission to create new user"}
                                    placement="top"
                                >
                                <span className={classes.editSpan}>
                                    <Button
                                        size="small"
                                        variant="contained"
                                        color="primary"
                                        className={canCreateUsers ? classes.addButton : classes.disabledButton}
                                        onClick={createUser}
                                    >
                                      + ADD NEW USER
                                    </Button>
                                </span>
                                </Tooltip>
                            </StyledLink>
                        </>
                    );
                }
                break;

            case 2:
                if (canCreateGroups) {
                    return (
                        <>
                            {showSearchBar(value)}
                            <StyledLink to="#">
                                <Button
                                    variant="contained"
                                    color="primary"
                                    disableElevation
                                    onClick={() => setCreateNewGroup(true)}
                                >
                                    + ADD NEW GROUP
                                </Button>
                            </StyledLink>
                        </>
                    );
                }
                break;

            case 3:
                return (
                    <div className={classes.alertBlock}>
                        {save ? (
                            <Alert className={classes.alert} variant="filled"
                                   severity="success">
                                Permissions Saved Successfully!
                            </Alert>
                        ) : ''}
                    </div>
                );

            case 5:
                return (
                    <StyledLink to="#">
                        <Button
                            variant="contained"
                            color="primary"
                            disableElevation
                            onClick={() => setNotification(true)}
                        >
                            + ADD NEW NOTIFICATION
                        </Button>
                    </StyledLink>
                );

            case 6:
                return companyCreateEnable ? (
                    <>
                        {showSearchBar(value)}
                        <StyledLink to="#">
                            <Tooltip
                                title={companyCreateEnable ? '' : "You don't have permission to create new company"}
                                placement="top"
                            >
                            <span className={classes.editSpan}>
                                <Button
                                    size="small"
                                    variant="contained"
                                    color="primary"
                                    className={companyCreateEnable ? classes.addButton : classes.disabledButton}
                                    onClick={createCompany}
                                >
                                  + ADD NEW COMPANY
                                </Button>
                            </span>
                            </Tooltip>
                        </StyledLink>
                    </>
                ) : null;

            default:
                return (<></>);
        }
    };

    let title;
    switch (parseInt(value)) {
        case 2:
            title = 'Manage Groups';
            break;
        case 3:
            title = 'Manage Permissions';
            break;
        case 4:
            title = 'Manage Roles';
            break;
        case 5:
            title = 'Manage Notifications';
            break;
        case 6:
            if (!companyCreateEnable) {
                setValue(0);
            } else {
                title = 'Manage Companies';
            }
            break;
        case 0:
        case 1 :
        default:
            if (enableCreateUser) {
                title = 'Create New User';
            } else {
                const user = JSON.parse(localStorage.getItem('user'));
                title = `Manage Members - ${user?.company_name}`;
            }
            break;
    }

    if (userDetails.role.toLowerCase() === 'agent') {
        return (
            <Redirect to="/dashboard"/>
        );
    }

    return (
        <div>
            <>
                {value === '3' && !edit ? (
                    <Tooltip
                        title={
                            !permissionCheck(
                                AdminsPermissions.permissions.create.task,
                                AdminsPermissions.permissions.create.type,
                            )
                                ? "You don't have permission to Enable/Disable Permissions"
                                : ''
                        }
                        style={{position: 'relative', float: 'right', 'isolation': 'isolate', 'zIndex': '99999'}}
                        placement="top"
                    >
                        <span className={classes.editSpan}>
                          <Button
                              variant="contained"
                              color="primary"
                              disableElevation
                              className={permissionCheck(
                                  AdminsPermissions.permissions.create.task,
                                  AdminsPermissions.permissions.create.type,
                              ) ? classes.saveBtn : classes.saveDisableBtn}
                              onClick={savePermissions}
                          >
                            Save
                          </Button>
                        </span>
                    </Tooltip>
                ) : ''}

                <Paper className={classes.paper}
                       style={{
                           left: '8em',
                           'maxWidth': 'calc(100vw - 10em)',
                           'top': '4em',
                           'position': 'sticky',
                           'isolation': 'isolate',
                           'zIndex': '100'
                       }}
                >
                    <Toolbar className={classes.root}>
                        <Typography
                            className={classes.title}
                            variant="h6"
                            id="tableTitle"
                            component="div"
                        >
                            {enableCreateUser
                                ? (
                                    <Breadcrumbs aria-label="breadcrumb">
                                        <StyledLink
                                            to="/dashboard"
                                            className={classes.breadcrumbs}
                                        >
                                            Dashboard
                                        </StyledLink>

                                        <StyledLink
                                            color="inherit"
                                            underline="always"
                                            className={classes.breadcrumbs}
                                            to="/admin"
                                            onClick={createUserData}
                                        >
                                            Employee list
                                        </StyledLink>
                                    </Breadcrumbs>
                                )
                                : ''}
                            {title}
                        </Typography>
                        {showSecondaryBar(value)}
                    </Toolbar>
                </Paper>
                <Paper className={empList.length > 0 ? '' : classes.paperHeight}>
                    {enableCreateUser
                        ? (
                            <div className={classes.createUserDiv}>
                                <CreateUser createUserData={createUserData}/>
                            </div>
                        )
                        : (
                            <TabContext value={value}>
                                <AppBar position="static" elevation={0} className={classes.navbar}>
                                    <TabList
                                        className={classes.tabList}
                                        onChange={handleTabs}
                                        TabIndicatorProps={{style: {backgroundColor: '#B07086'}}}
                                    >
                                        <Tab
                                            label="EMPLOYEE LIST"
                                            value="0"
                                            className={
                                                permissionCheck(
                                                    AdminsPermissions.employee_list.create.task,
                                                    AdminsPermissions.employee_list.create.type,
                                                ) || permissionCheck(
                                                    AdminsPermissions.employee_list.view.task,
                                                    AdminsPermissions.employee_list.view.type,
                                                ) || permissionCheck(
                                                    AdminsPermissions.employee_list.update.task,
                                                    AdminsPermissions.employee_list.update.type,
                                                )
                                                    ? value === '0' ? classes.activeTab : classes.tab
                                                    : classes.groupHide
                                            }
                                        />
                                        {/* <Tab
                    label="Product Access"
                    value="1"
                    className={value === '1' ? classes.activeTab : classes.tab}
                  /> */}

                                        <Tab
                                            label="GROUPS"
                                            value="2"
                                            className={
                                                (permissionCheck(
                                                    AdminsPermissions.groups.create.task,
                                                    AdminsPermissions.groups.create.type,
                                                ) || permissionCheck(
                                                    AdminsPermissions.groups.view.task,
                                                    AdminsPermissions.groups.view.type,
                                                ))
                                                    ? value === '2' ? classes.activeTab : classes.tab : classes.groupHide
                                            }
                                        />

                                        <Tab
                                            label="PERMISSIONS"
                                            value="3"
                                            className={
                                                permissionCheck(
                                                    AdminsPermissions.permissions.create.task,
                                                    AdminsPermissions.permissions.create.type,
                                                ) || permissionCheck(
                                                    AdminsPermissions.permissions.view.task,
                                                    AdminsPermissions.permissions.view.type,
                                                ) || permissionCheck(
                                                    AdminsPermissions.permissions.update.task,
                                                    AdminsPermissions.permissions.update.type,
                                                )
                                                    ? value === '3' ? classes.activeTab : classes.tab
                                                    : classes.groupHide
                                            }
                                        />
                                        <Tab
                                            label="ROLES"
                                            value="4"
                                            className={(permissionCheck(
                                                AdminsPermissions.roles.create.task,
                                                AdminsPermissions.roles.create.type,
                                            ) || permissionCheck(
                                                AdminsPermissions.roles.view.task,
                                                AdminsPermissions.roles.view.type,
                                            ) || permissionCheck(
                                                AdminsPermissions.roles.update.task,
                                                AdminsPermissions.roles.update.type,
                                            ) || permissionCheck(
                                                AdminsPermissions.roles.delete.task,
                                                AdminsPermissions.roles.delete.type,
                                            ))
                                                ? value === '4' ? classes.activeTab : classes.tab : classes.groupHide}
                                        />
                                        <Tab
                                            label="NOTIFICATIONS"
                                            value="5"
                                            className={value === '5' ? classes.activeTab : classes.tab}
                                        />
                                        {companyCreateEnable ? (
                                            <Tab
                                                label="COMPANIES"
                                                value="6"
                                                className={value === '6' ? classes.activeTab : classes.tab}
                                            />
                                        ) : (
                                            <Tab
                                                label=""
                                                value="6"
                                                className={classes.groupHide}
                                            />
                                        )}
                                    </TabList>
                                </AppBar>
                                <TabPanel key={0} value="0" className={classes.tabPanel}>
                                    <EmployeeList
                                        empList={empList}
                                        total={employeeTotal}
                                        addTempRole={addTempRole}
                                        handleNext={handleNext}
                                        createUser={createUser}
                                        deleteEmployee={deleteEmployee}
                                        viewPermission={viewPermission}
                                        sortProperties={sortProperties}
                                        sortBy={sortBy}
                                        order={order}
                                    />
                                </TabPanel>
                                <TabPanel key={2} value="2" className={classes.tabPanel}>
                                    {createNewGroup ? <CreateGroup createGroup={createGroup} prop={prop}/>
                                        : (
                                            permissionCheck(
                                                AdminsPermissions.groups.view.task,
                                                AdminsPermissions.groups.view.type,
                                            )
                                                ? (
                                                    <Groups
                                                        groupList={groupList}
                                                        total={groupTotal}
                                                        handleNext={handleNext}
                                                        sortProperties={sortProperties}
                                                        sortBy={sortBy}
                                                        order={order}
                                                    />
                                                )
                                                : ''
                                        )}
                                </TabPanel>
                                <TabPanel key={4} value="4" className={classes.tabPanel}>
                                    <Roles/>
                                </TabPanel>
                                <TabPanel value="3" className={classes.tabPanel}>
                                    <Permissions
                                        handleChange={handleChange}
                                        editClick={editClick}
                                        roles={roles}
                                        refreshPermission={refreshPermission}
                                    />
                                </TabPanel>
                                <TabPanel value="5" className={classes.tabPanel}>
                                    <Notification
                                        notification={notification}
                                        notificationClose={notificationClose}
                                    />
                                </TabPanel>
                                {companyCreateEnable ? (
                                    <TabPanel value="6" className={classes.tabPanel}>
                                        <ErrorBoundary>
                                            <CompanyTable showEditor={setShowEditCompanyModal}
                                                          addingCompany={showEditCompanyModal}
                                                          searchText={textSearch}
                                            />
                                        </ErrorBoundary>
                                    </TabPanel> ) : (
                                    <TabPanel value="6" className={classes.hideGroup}>
                                    </TabPanel>
                                ) }
                            </TabContext>
                        )}
                </Paper>
            </>
        </div>
    );
};

Admin.propTypes = {
    fetchEmployeeList: PropTypes.func,
    employeeList: PropTypes.array,
    employeeTotal: PropTypes.number,
    fetchGroupsList: PropTypes.func,
    groups: PropTypes.array,
    groupTotal: PropTypes.number,
    prop: PropTypes.object,
    reset: PropTypes.func,
    fetchPermissionDetails: PropTypes.func,
    userDetails: PropTypes.object,
};

/**
 * Maps Redux state to the Admin component props.
 *
 * @param {Object} state - The Redux store state.
 * @param {Object} props - The component's own props.
 * @returns {Object} Props derived from state.
 */
const mapStateToProps = (state, props) => ({
    employeeList: state.sim.employeeList.result ? state.sim.employeeList.result : [],
    employeeTotal: state.sim.employeeList.total,
    groups: state.sim.result && state.sim.result.groups ? state.sim.result.groups : [],
    groupTotal: state.sim.result ? state.sim.result.total : 0,
    prop: props ? props.location.state : {},
    permissions: state.login.permissions,
    userDetails: state.login.user,
});

/**
 * Maps Redux dispatch to the Admin component props.
 *
 * @type {Object}
 */
const mapDispatchToProps = {
    fetchEmployeeList, fetchGroupsList, fetchPermissionDetails, reset,
};

export default connect(mapStateToProps, mapDispatchToProps)(Admin);
